import React from 'react';
import { Button } from '@/components/ui/button';
import { 
  Smartphone, 
  Download, 
  Apple, 
  PlayCircle, 
  CheckCircle2, 
  Shield, 
  Zap, 
  Bell,
  QrCode,
  ExternalLink,
  Book,
  ChevronRight,
  Users,
  Home
} from 'lucide-react';

const MobileAppPage = () => {
  // Host App (iHostPMS)
  const HOST_ANDROID_APK_URL = 'https://expo.dev/artifacts/eas/creVoYwAHUoKgMF3a1WFy7.apk';
  const HOST_IOS_TESTFLIGHT_URL = null;
  
  // Guest App (iStayBnB)
  const GUEST_ANDROID_APK_URL = 'https://expo.dev/artifacts/eas/mwkRuexrysrgRydZ6srQJ5.apk';
  const GUEST_IOS_TESTFLIGHT_URL = null;
  
  const features = [
    {
      icon: <Zap className="w-5 h-5 text-amber-500" />,
      title: 'Fast Loading',
      description: 'Optimized for speed with native performance'
    },
    {
      icon: <Shield className="w-5 h-5 text-emerald-500" />,
      title: 'Persistent Login',
      description: 'Stay logged in securely using device encryption'
    },
    {
      icon: <Bell className="w-5 h-5 text-blue-500" />,
      title: 'Push Notifications',
      description: 'Get instant alerts for new bookings and updates'
    },
    {
      icon: <Smartphone className="w-5 h-5 text-purple-500" />,
      title: 'Native Experience',
      description: 'Built with React Native for iOS and Android'
    },
  ];

  const installSteps = {
    android: [
      'Click the "Download APK" button below',
      'Open the downloaded file on your Android device',
      'If prompted, allow installation from unknown sources',
      'Follow the on-screen instructions to complete installation',
      'Open iHostPMS and log in with your account'
    ],
    ios: [
      'Click the "TestFlight" button below',
      'Install TestFlight from the App Store if not already installed',
      'Accept the beta testing invitation',
      'Install iHostPMS from TestFlight',
      'Open iHostPMS and log in with your account'
    ]
  };

  return (
    <div className="animate-fade-in" data-testid="mobile-app-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-airbnb-gray flex items-center gap-3">
          <Smartphone className="w-7 h-7 text-airbnb-pink" />
          Mobile Apps
        </h1>
        <p className="text-airbnb-medium-gray mt-1">
          Download our mobile apps for hosts and guests
        </p>
      </div>

      {/* iHostPMS Host App */}
      <div className="bg-gradient-to-br from-airbnb-pink to-rose-600 rounded-2xl p-6 lg:p-8 text-white mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Home className="w-5 h-5" />
              <span className="text-sm font-medium text-white/80">For Property Hosts</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold mb-2">iHostPMS Mobile</h2>
            <p className="text-white/80 text-sm lg:text-base mb-4">
              Manage your properties on the go. Access bookings, check availability, 
              and stay connected with your guests from anywhere.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                Version 1.0.0
              </span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                React Native
              </span>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="w-24 h-24 lg:w-32 lg:h-32 bg-white/20 rounded-2xl flex items-center justify-center">
              <Smartphone className="w-12 h-12 lg:w-16 lg:h-16" />
            </div>
          </div>
        </div>
      </div>

      {/* Host App Downloads */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
        {/* Android */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center">
              <PlayCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray">Android (Host)</h3>
              <p className="text-sm text-airbnb-medium-gray">APK Download</p>
            </div>
          </div>
          
          {HOST_ANDROID_APK_URL ? (
            <a 
              href={HOST_ANDROID_APK_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl">
                <Download className="w-4 h-4 mr-2" />
                Download APK
              </Button>
            </a>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800 font-medium mb-1">Coming Soon</p>
              <p className="text-xs text-amber-600">
                The Android APK is being prepared.
              </p>
            </div>
          )}
        </div>

        {/* iOS */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-gray-50 rounded-xl flex items-center justify-center">
              <Apple className="w-8 h-8 text-gray-800" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray">iOS (Host)</h3>
              <p className="text-sm text-airbnb-medium-gray">TestFlight Beta</p>
            </div>
          </div>
          
          {HOST_IOS_TESTFLIGHT_URL ? (
            <a 
              href={HOST_IOS_TESTFLIGHT_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-gray-800 hover:bg-gray-900 text-white rounded-xl">
                <Apple className="w-4 h-4 mr-2" />
                Open TestFlight
              </Button>
            </a>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800 font-medium mb-1">Coming Soon</p>
              <p className="text-xs text-amber-600">
                iOS TestFlight build requires Apple Developer credentials.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* iStayBnB Guest App */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 lg:p-8 text-white mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5" />
              <span className="text-sm font-medium text-white/80">For Guests</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold mb-2">iStayBnB</h2>
            <p className="text-white/80 text-sm lg:text-base mb-4">
              Find and book amazing stays. Browse properties, save favorites, 
              and manage your trips all in one elegant Airbnb-style app.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                Version 1.0.0
              </span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                React Native
              </span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                M-Pesa & Pesapal
              </span>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="w-24 h-24 lg:w-32 lg:h-32 bg-white/20 rounded-2xl flex items-center justify-center">
              <Users className="w-12 h-12 lg:w-16 lg:h-16" />
            </div>
          </div>
        </div>
      </div>

      {/* Guest App Downloads */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Android */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center">
              <PlayCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray">Android (Guest)</h3>
              <p className="text-sm text-airbnb-medium-gray">iStayBnB APK</p>
            </div>
          </div>
          
          {GUEST_ANDROID_APK_URL ? (
            <a 
              href={GUEST_ANDROID_APK_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
                <Download className="w-4 h-4 mr-2" />
                Download iStayBnB APK
              </Button>
            </a>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800 font-medium mb-1">Coming Soon</p>
              <p className="text-xs text-amber-600">
                The Android APK is being prepared.
              </p>
            </div>
          )}
        </div>

        {/* iOS */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-gray-50 rounded-xl flex items-center justify-center">
              <Apple className="w-8 h-8 text-gray-800" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-airbnb-gray">iOS (Guest)</h3>
              <p className="text-sm text-airbnb-medium-gray">iStayBnB TestFlight</p>
            </div>
          </div>
          
          {GUEST_IOS_TESTFLIGHT_URL ? (
            <a 
              href={GUEST_IOS_TESTFLIGHT_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-gray-800 hover:bg-gray-900 text-white rounded-xl">
                <Apple className="w-4 h-4 mr-2" />
                Open TestFlight
              </Button>
            </a>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800 font-medium mb-1">Coming Soon</p>
              <p className="text-xs text-amber-600">
                iOS TestFlight build requires Apple Developer credentials.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8">
        <h3 className="text-lg font-bold text-airbnb-gray mb-4">Key Features</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature, index) => (
            <div key={index} className="bg-gray-50 rounded-xl p-4">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mb-3 shadow-sm">
                {feature.icon}
              </div>
              <h4 className="font-semibold text-airbnb-gray text-sm mb-1">{feature.title}</h4>
              <p className="text-xs text-airbnb-medium-gray">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Installation Guide */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Android Instructions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <PlayCircle className="w-5 h-5 text-green-600" />
            <h3 className="font-bold text-airbnb-gray">Android Installation</h3>
          </div>
          <ol className="space-y-3">
            {installSteps.android.map((step, index) => (
              <li key={index} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </span>
                <span className="text-sm text-airbnb-medium-gray">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* iOS Instructions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Apple className="w-5 h-5 text-gray-800" />
            <h3 className="font-bold text-airbnb-gray">iOS Installation</h3>
          </div>
          <ol className="space-y-3">
            {installSteps.ios.map((step, index) => (
              <li key={index} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </span>
                <span className="text-sm text-airbnb-medium-gray">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* App Screenshots Placeholder */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8">
        <h3 className="text-lg font-bold text-airbnb-gray mb-4">App Preview</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {['Dashboard', 'Bookings', 'Properties', 'Profile'].map((screen, index) => (
            <div key={index} className="aspect-[9/16] bg-gray-100 rounded-xl flex items-center justify-center">
              <div className="text-center">
                <Smartphone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <span className="text-xs text-airbnb-medium-gray">{screen}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-airbnb-medium-gray text-center mt-4">
          Screenshots will be available after the first build
        </p>
      </div>

      {/* Technical Documentation */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-airbnb-gray mb-4 flex items-center gap-2">
          <Book className="w-5 h-5 text-blue-500" />
          Technical Documentation
        </h3>
        
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <h4 className="font-semibold text-blue-800 text-sm mb-2">Technology Stack</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• <strong>Framework:</strong> React Native with Expo (SDK 55)</li>
              <li>• <strong>Navigation:</strong> Expo Router (file-based routing)</li>
              <li>• <strong>Auth:</strong> JWT with SecureStore encryption</li>
              <li>• <strong>State:</strong> React Context API</li>
              <li>• <strong>API:</strong> REST API connecting to iHostPMS backend</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-semibold text-airbnb-gray text-sm mb-2">Build Information</h4>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <p className="text-airbnb-medium-gray">Build System</p>
                <p className="font-medium text-airbnb-gray">EAS Build</p>
              </div>
              <div>
                <p className="text-airbnb-medium-gray">Distribution</p>
                <p className="font-medium text-airbnb-gray">Internal Testing</p>
              </div>
              <div>
                <p className="text-airbnb-medium-gray">Android Format</p>
                <p className="font-medium text-airbnb-gray">APK (universal)</p>
              </div>
              <div>
                <p className="text-airbnb-medium-gray">iOS Format</p>
                <p className="font-medium text-airbnb-gray">TestFlight Beta</p>
              </div>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <h4 className="font-semibold text-emerald-800 text-sm mb-2">Security Features</h4>
            <ul className="text-xs text-emerald-700 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                Encrypted token storage using device Keychain/Keystore
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                Secure HTTPS API communication
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                Automatic session timeout for inactive users
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                No sensitive data stored in plain text
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAppPage;
